import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  BasicEventInfoDocument,
  EventWebsiteGuestDocument,
  GetEventCTAButtonsDataDocument,
  GetGuestsiteCoupleStoryAndQandasDocument,
  GetGuestSiteScheduleDocument,
  useJoinEventAsPersonWithPasswordMutation,
} from '@graphql/generated';
import { performFullPageRedirect } from '@shared/utils/navigation';
import { useEffect } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';

interface UnlockFields
  extends Readonly<{
    password: string;
  }> {}

interface UseUnlockControllerProps {
  eventId: string;
  onUnlockSuccess: () => void;
}

export function useUnlockController({ eventId, onUnlockSuccess }: UseUnlockControllerProps) {
  const { toast } = useToast();
  const [joinEvent] = useJoinEventAsPersonWithPasswordMutation();
  const { tq } = useTranslation('guestUnlock');

  const formik = useFormik<UnlockFields>({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object<UnlockFields>({
      password: Yup.string().required(tq`Password’s required`),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      try {
        const responseJoinEvent = await joinEvent({
          variables: {
            eventId,
            payload: { eventPassword: values.password },
          },
          refetchQueries: () => [
            BasicEventInfoDocument,
            GetGuestsiteCoupleStoryAndQandasDocument,
            GetEventCTAButtonsDataDocument,
            EventWebsiteGuestDocument,
            GetGuestSiteScheduleDocument,
          ],
          awaitRefetchQueries: true,
        });
        if (responseJoinEvent?.data?.joinEventAsPersonWithPassword?.hasUnlocked) {
          onUnlockSuccess();
        }
      } catch (e) {
        formik.setFieldError('password', 'incorrect');
      }
    },
  });

  const onSignInHandler = () => {
    performFullPageRedirect('/login');
  };

  const inputError = formik.errors.password;

  useEffect(() => {
    if (inputError === 'incorrect') {
      toast(tq`Wrong password. Double-check what you’ve typed, or try copying and pasting.`);
    }

    if (inputError && inputError !== 'incorrect') {
      toast(inputError);
    }
  }, [inputError, toast, tq]);

  return {
    formik,
    loading: formik.isSubmitting,

    inputError: formik.errors.password,
    onSignInHandler,
  };
}
